import React, { memo, useState } from "react";
import Divider from "@material-ui/core/Divider";
import { OXYGEN_ID } from "utils/form.utils";
import { makeStyles, createStyles } from "@material-ui/styles";
import { ValmetRed6, ValmetGrey10, ValmetGrey4 } from "../../theme";
import { Skeleton } from "@material-ui/lab";
import { Typography } from "@material-ui/core";

interface Props {
  id: string;
  title: string;
  min: number;
  isDisabled: boolean;
  isValid?: boolean;
  loading: boolean;
  value: number;
  handleValueChange(id: string, value: string): void;
}

const localStyles = makeStyles(() =>
  createStyles({
    input: {
      border: `1px solid ${ValmetGrey10}`,
      boxSizing: "border-box",
      width: "100%",
      outline: "none",
      padding: "5px",
      borderRadius: "3px",
    },
    inputContainer: {
      display: "flex",
      alignItems: "center",
    },
    inputTitleCell: {
      width: "55%",
    },
    inputTitle: {
      color: ValmetGrey4,
      textAlign: "right",
      lineHeight: 1,
      letterSpacing: "0.00938em",
      paddingRight: "10px",
    },
    percentCell: {
      padding: "5px",
      color: ValmetGrey4,
    },
    erroredBorders: {
      border: `1px solid ${ValmetRed6}`,
    },
    erroredText: {
      color: ValmetRed6,
    },
    dividerContainer: {
      margin: "10px 0 10px 0",
    },
    disabledInput: {
      color: ValmetGrey10,
    },
  }),
);

const FuelCompositionInput: React.FC<Props> = ({ id, title, min, isDisabled, isValid, loading, value, handleValueChange }) => {
  const localClasses = localStyles();

  const [editing, setEditing] = useState(false);
  const [blurValue, setblurValue] = useState((Math.round(value * 100) / 100).toFixed(2));

  const handleInputChange = (event: React.FormEvent<HTMLInputElement>) => {
    setEditing(true);
    handleValueChange(id, event.currentTarget.value);
  };

  // Set input float to always have two decimals
  const handleInputBlur = (event: React.FormEvent<HTMLInputElement>) => {
    setEditing(false);

    const inputValue = parseFloat(event.currentTarget.value);

    if (!isNaN(inputValue)) {
      setblurValue((Math.round(inputValue * 100) / 100).toFixed(2));
    } else {
      setblurValue("0.00");
    }
  };

  const setInputValue = () => {
    if (id === OXYGEN_ID) {
      return (Math.round(value * 100) / 100).toFixed(2);
    }

    return !editing ? blurValue : isNaN(value) ? "" : value;
  };

  const chooseSign = () => {
    switch (id) {
      case "HHV":
        return "MJ/kg ds";
      case "CNCG_HHV":
        return "MJ/Nm3";
      default:
        return "%";
    }
  };

  return (
    <>
      <div className={localClasses.inputContainer}>
        <div className={localClasses.inputTitleCell}>
          <Typography
            variant="body2"
            className={isValid ? localClasses.inputTitle : `${localClasses.inputTitle} ${localClasses.erroredText}`}
          >
            {title}
          </Typography>
          {/* <p className={isValid ? localClasses.inputTitle : `${localClasses.inputTitle} ${localClasses.erroredText}`}></p> */}
        </div>
        <div>
          {loading ? (
            <Skeleton variant="rect" />
          ) : (
            <input
              disabled={loading || isDisabled || id === OXYGEN_ID} // The Oxygen field should remain disabled
              required
              type="number"
              step="0.01"
              value={setInputValue()}
              onKeyPress={(event) => {
                event.key === "Enter" && event.preventDefault();
              }}
              onChange={(event) => handleInputChange(event)}
              onBlur={(event) => handleInputBlur(event)}
              className={
                isValid
                  ? isDisabled
                    ? `${localClasses.input} ${localClasses.disabledInput}`
                    : localClasses.input
                  : `${localClasses.input} ${localClasses.erroredBorders} ${localClasses.erroredText}`
              }
            />
          )}
        </div>
        <Typography variant="caption" className={localClasses.percentCell}>
          {chooseSign()}
        </Typography>
      </div>
      {id === OXYGEN_ID ? (
        <div className={localClasses.dividerContainer}>
          <Divider />
        </div>
      ) : null}
    </>
  );
};

export default memo(FuelCompositionInput);
